import {
  appendQuery,
  buildCellServiceUrl,
  resolveCellIdFromIdToken as resolveCellIdFromIdTokenShared,
  type CellServiceQuery,
} from './cell-session';
import { isAbortError, withTokenAuthRecovery } from './cors-auth-recovery';

// =============================================================================
// Client base
// =============================================================================
//
// Direct browser-side calls to ``https://{cellId}.api.tranquility.ai``
// ``/provenance-service/v1/*``. This is the only client for the
// provenance-service: the legacy ``/api/protected/provenance-service-proxy``
// BFF routes have been removed. All UI consumers (EvidenceLocker file list,
// case dropdown, source-info / file-info lookups, status counts, file status
// hover) call into this module using the access token resolved by
// ``useCellServiceSession``.

/** Path prefix for every provenance-service v1 endpoint. */
const API_PREFIX = '/provenance-service/v1';

/**
 * Error thrown by every direct-CORS call to provenance-service. Carries the
 * upstream HTTP status (when available) and the raw response body for
 * downstream loggers.
 */
export class ProvenanceApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'ProvenanceApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Re-exported for backwards compatibility — the canonical implementation now
 * lives in ``shared/cell-session.ts`` so it can be shared across direct-CORS
 * service clients (provenance, junkdrawer, …).
 */
export const resolveCellIdFromIdToken = resolveCellIdFromIdTokenShared;

/**
 * Build a fully-qualified provenance-service URL for a given cell.
 *
 * ``path`` is the route *after* ``/provenance-service/v1`` (e.g. ``"/cases"``,
 * ``"/files"``). A leading slash is optional.
 */
export function buildProvenanceUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

/** Query map: scalar values are emitted once; arrays are emitted as repeated keys. */
export type ProvenanceQuery = CellServiceQuery;

type ProvenanceFetchArgs = {
  /** ``custom:cell_id`` claim, used to build the per-cell API host. */
  cellId: string;
  /**
   * Real Cognito access token (``token_use: "access"``). In this app that's
   * ``AuthContext.realAccessToken`` / ``features.tokens.rawAccessToken`` —
   * NOT ``AuthContext.accessToken`` (which is the ID token for legacy BFF
   * cookie reasons and would be rejected by the Lambda Authorizer).
   */
  accessToken: string;
  /** Path under ``/provenance-service/v1`` (e.g. ``"/cases"``). */
  path: string;
  /** Optional query map; arrays produce repeated keys. */
  query?: ProvenanceQuery;
  signal?: AbortSignal;
};

/**
 * Generic GET helper for provenance-service. Validates the session inputs,
 * issues the request, and either returns parsed JSON typed as ``T`` or throws
 * a ``ProvenanceApiError``.
 */
export async function provenanceFetch<T>(args: ProvenanceFetchArgs): Promise<T> {
  const { cellId, path, query, signal } = args;

  if (!cellId) {
    throw new ProvenanceApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!args.accessToken) {
    throw new ProvenanceApiError('Missing access token.');
  }

  const url = appendQuery(buildProvenanceUrl(cellId, path), query);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new ProvenanceApiError(
        `Network error calling provenance-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new ProvenanceApiError(
        `provenance-service ${path} returned ${res.status}`,
        res.status,
        detail || null,
      );
    }

    return (await res.json().catch(() => null)) as T;
  });
}

type ProvenancePutArgs = {
  cellId: string;
  accessToken: string;
  /** Path under ``/provenance-service/v1`` (e.g. ``"/content-moderation/findings/abc/status"``). */
  path: string;
  body: unknown;
  /** Optional query map; arrays produce repeated keys. */
  query?: ProvenanceQuery;
  signal?: AbortSignal;
};

/**
 * Generic PUT helper for provenance-service. Mirrors ``provenanceFetch`` but
 * sends a JSON body and uses the PUT method. Throws ``ProvenanceApiError`` on
 * network or HTTP errors.
 */
export async function provenancePut<T>(args: ProvenancePutArgs): Promise<T> {
  const { cellId, accessToken, path, body, query, signal } = args;

  if (!cellId) {
    throw new ProvenanceApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new ProvenanceApiError('Missing access token.');
  }

  const url = appendQuery(buildProvenanceUrl(cellId, path), query);

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    throw new ProvenanceApiError(
      `Network error calling provenance-service ${path}: ${(error as Error).message}`,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new ProvenanceApiError(
      `provenance-service ${path} returned ${res.status}`,
      res.status,
      detail || null,
    );
  }

  return (await res.json().catch(() => null)) as T;
}

type ProvenancePostArgs = {
  cellId: string;
  accessToken: string;
  /** Path under ``/provenance-service/v1`` (e.g. ``"/csam-audit/queries"``). */
  path: string;
  body: unknown;
  signal?: AbortSignal;
};

/**
 * Generic POST helper for provenance-service. Mirrors ``provenancePut`` but
 * uses the POST method. Throws ``ProvenanceApiError`` on network/HTTP errors.
 */
export async function provenancePost<T>(args: ProvenancePostArgs): Promise<T> {
  const { cellId, accessToken, path, body, signal } = args;

  if (!cellId) {
    throw new ProvenanceApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new ProvenanceApiError('Missing access token.');
  }

  const url = buildProvenanceUrl(cellId, path);

  return withTokenAuthRecovery(accessToken, async (token) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new ProvenanceApiError(
        `Network error calling provenance-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new ProvenanceApiError(
        `provenance-service ${path} returned ${res.status}`,
        res.status,
        detail || null,
      );
    }

    return (await res.json().catch(() => null)) as T;
  });
}

// =============================================================================
// Cases endpoints
// =============================================================================

/**
 * Flat summary of a case returned by ``GET /v1/cases``.
 *
 * - ``caseName`` — human display label. The case dropdown uses this as both
 *   value and label so UUID-folder cases still show a name, not a UUID.
 * - ``caseTag`` — opaque ``case:<uuid>`` or ``case:<name>`` filter sent to
 *   provenance / evidence / timeline. Resolve it with ``resolveCaseTag``;
 *   do not re-derive ``case:${name}``.
 * - ``folderSegment`` — the S3 folder name from permission-service
 *   (``folder_segment``). Present on the permissioned list; absent on the
 *   legacy provenance list, which still recovers it from the tag. Use this
 *   verbatim for uploads and other case-sensitive keys. Do not lowercase it
 *   and do not rebuild it from ``caseTag``.
 */
export interface CaseSummary {
  caseName: string;
  caseTag: string;
  folderSegment?: string;
  /** Permission-service case CSAM flag (TR-2631). Absent on the provenance list. */
  csamFlagged?: boolean;
  csamFlagSource?: 'auto' | 'manual' | null;
  csamCanTurnOff?: boolean;
}

type FetchCasesListArgs = {
  cellId: string;
  accessToken: string;
  signal?: AbortSignal;
};

/** Fetch the flat list of cases via direct CORS to the cell gateway. */
export async function fetchCasesList(args: FetchCasesListArgs): Promise<CaseSummary[]> {
  const data = await provenanceFetch<unknown>({
    cellId: args.cellId,
    accessToken: args.accessToken,
    path: '/cases',
    signal: args.signal,
  });

  if (!Array.isArray(data)) {
    throw new ProvenanceApiError('provenance-service /cases returned a non-array body.');
  }

  return data
    .map((raw) => {
      const r = raw as Record<string, unknown>;
      const caseName = typeof r.caseName === 'string' ? r.caseName : '';
      const caseTag = typeof r.caseTag === 'string' ? r.caseTag : '';
      return { caseName, caseTag };
    })
    .filter((c) => c.caseTag.trim() !== '');
}

// =============================================================================
// Files endpoints
// =============================================================================

/** Whether ``tags`` filtering is OR-style (``any``) or AND-style (``all``). */
export type TagMatch = 'any' | 'all';

/**
 * Filter set for ``GET /v1/files``. Field names mirror the FastAPI route
 * (snake_case) but are exposed as camelCase here; ``fetchProvenanceFilesPage``
 * handles the translation.
 */
export interface ProvenanceFilesFilters {
  searchQuery?: string;
  includeDeleted?: boolean;
  contentCategories?: string[];
  tags?: string[];
  tagMatch?: TagMatch;
}

/**
 * Lifecycle status reported for an ingested file. Known values are kept as a
 * literal union for autocomplete; the ``(string & {})`` widening lets new
 * server-side statuses still type-check at runtime without forcing a frontend
 * release.
 */
export type FileStatus = 'COMPLETED' | 'FAILED' | 'DELIVERY' | (string & {});

/**
 * Single item returned in the ``items`` array of ``GET /v1/files``. Mirrors
 * the backend ``FileSummary`` shape (camelCase). All timestamp fields are
 * ISO-8601 strings; numeric ``height`` / ``width`` / ``durationInSeconds``
 * are nullable for non-image / non-media uploads.
 *
 * As of the dev cutover this is *identical* to the ``file`` payload returned
 * inside ``ProvenanceFileInfoBody`` (``/files/file-info`` and the ``fileInfo``
 * blob embedded in tree nodes), so consumers can treat the three sources
 * interchangeably — see ``ProvenanceFileInfoFile`` below, which is exported
 * as an alias.
 */
export interface FileSummary {
  createdAt: string;
  updatedAt: string;
  id: string;
  fileStatusId: string;
  fileUuid: string;
  searchablePath: string;
  fileName: string;
  /**
   * Canonical ``s3://<bucket>/<key>`` URL for the file. Newly added to the
   * top-level ``FileSummary`` shape — previously only available through the
   * loose ``metadata.file_url`` blob on ``/tree`` responses.
   */
  fileUrl: string;
  tags: string[];
  contentCategories: string[];
  status: FileStatus;
  statusMessage: string;
  subdocumentCount: number;
  subdocumentSuccessCount: number;
  subdocumentFailureCount: number;
  subdocumentSkipCount: number;
  countersLastUpdatedAt: string;
  lastUpdateSentAt: string;
  md5: string;
  blake3: string;
  xxhash: string;
  size: number;
  mimeType: string;
  extension: string;
  height: number | null;
  width: number | null;
  format: string | null;
  durationInSeconds: number | null;
  /** Page number when the file represents one page of a larger document; null otherwise. */
  pageNumber: number | null;
  pageCount: number | null;
  hasAudioStream: boolean | null;
  hasVideoStream: boolean | null;
  hasTranscript: boolean | null;
  isImage: boolean | null;
  previewUrl: string | null;
  downloadUrl: string | null;
  transcriptUrl: string | null;
  contentModeration: ContentModeration | null;
}

/** Async blur job after a user flags a file (TR-3033 / TR-3168). */
export type ManualObfuscationJobStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export function isManualObfuscationInProgress(
  contentModeration?: ContentModeration | null,
): boolean {
  return contentModeration?.manualObfuscationJobStatus === 'IN_PROGRESS';
}

export function isManualObfuscationComplete(
  contentModeration?: ContentModeration | null,
): boolean {
  return contentModeration?.manualObfuscationJobStatus === 'COMPLETED';
}

/**
 * Content-moderation metadata attached to a flagged file.
 *
 * ``type`` is the detection type; ``findingStatus`` is the review-workflow
 * state. A ``REVIEWED`` finding is considered cleared — consumers should not
 * mark the file as flagged content. The obfuscated/original URLs are surfaced
 * through the top-level ``previewUrl`` field (safe by default) and the
 * ``bypass_cm`` query param on ``GET /file-info`` (returns original).
 */
export interface ContentModeration {
  type: string | null;
  findingStatus: FindingStatusValue | null;
  /**
   * LLM-generated natural-language description of the flagged image, mirrored
   * from the content-moderation finding onto file-info so media previews can let
   * a reviewer assess the content before revealing the original. Nullable — not
   * every flagged file has a generated description.
   */
  imageDescription?: string | null;
  /**
   * Present on user-flagged files while provenance runs the async blur.
   * ``IN_PROGRESS`` is the spinner state; ``COMPLETED`` means the obfuscated
   * preview is ready; ``FAILED`` means the blur worker gave up. Omitted for
   * ingest-time findings.
   */
  manualObfuscationJobStatus?: ManualObfuscationJobStatus | null;
}

/** Pagination block returned alongside ``items`` by ``GET /v1/files``. */
export interface FilesPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

/** Full response body for ``GET /v1/files``. */
export interface FilesListResponse {
  items: FileSummary[];
  pagination: FilesPagination;
}

/**
 * A single page returned by the offset/limit-paginated files list, normalized
 * for ``useInfiniteQuery`` consumption.
 *
 * ``nextOffset`` is ``null`` when the upstream's ``pagination.hasMore`` is
 * ``false``; otherwise it is the offset to request next.
 */
export interface ProvenanceFilesPage {
  items: FileSummary[];
  /** ``null`` when there are no more pages (``pagination.hasMore === false``). */
  nextOffset: number | null;
  /** Total number of items matching the query, from ``pagination.total``. */
  total: number;
}

type FetchProvenanceFilesPageArgs = {
  cellId: string;
  accessToken: string;
  offset: number;
  limit: number;
  filters: ProvenanceFilesFilters;
  signal?: AbortSignal;
};

function filesQueryFromFilters(
  offset: number,
  limit: number,
  filters: ProvenanceFilesFilters,
): ProvenanceQuery {
  const q: ProvenanceQuery = {
    offset,
    limit,
  };
  if (filters.searchQuery && filters.searchQuery.trim() !== '') {
    q.searchQuery = filters.searchQuery;
  }
  if (typeof filters.includeDeleted === 'boolean') {
    q.includeDeleted = filters.includeDeleted;
  }
  if (filters.contentCategories && filters.contentCategories.length > 0) {
    q.contentCategories = filters.contentCategories;
  }
  if (filters.tags && filters.tags.length > 0) {
    q.tags = filters.tags;
  }
  if (filters.tagMatch) {
    q.tagMatch = filters.tagMatch;
  }
  return q;
}

/**
 * Direct-CORS fetch for one page of files. Translates the upstream
 * ``{ items, pagination }`` shape into a normalized ``ProvenanceFilesPage``
 * so callers (e.g. ``useInfiniteQuery``) can chain pages off ``nextOffset``
 * alone.
 */
export async function fetchProvenanceFilesPage(
  args: FetchProvenanceFilesPageArgs,
): Promise<ProvenanceFilesPage> {
  const { cellId, accessToken, offset, limit, filters, signal } = args;

  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: '/files',
    query: filesQueryFromFilters(offset, limit, filters),
    signal,
  });

  if (!body || typeof body !== 'object' || !Array.isArray((body as FilesListResponse).items)) {
    throw new ProvenanceApiError(
      'provenance-service /files returned an unexpected body (expected { items, pagination }).',
    );
  }

  const parsed = body as FilesListResponse;
  const items = parsed.items;
  const pagination = parsed.pagination;

  const hasMore = pagination?.hasMore === true;
  const total = typeof pagination?.total === 'number' ? pagination.total : items.length;
  const nextOffset = hasMore ? offset + items.length : null;

  return {
    items,
    nextOffset,
    total,
  };
}

// =============================================================================
// Tree endpoints
// =============================================================================
//
// The unified tree API exposes a hierarchical view over file_status +
// subdocument rows. Used by the EvidenceLocker tree picker to navigate
// directories and search files across the cell.

/** View mode for tree responses: ``user`` hides internal-only subdocuments. */
export type TreeView = 'user' | 'engineering';

/** Node kinds returned by ``/tree`` and ``/tree/search``. */
export type TreeNodeType = 'directory' | 'file' | 'subdocument';

/**
 * Lightweight reference to the parent source file, attached to ``subdocument``
 * nodes returned by ``/tree/search`` (used to render the containing-file
 * breadcrumb). camelCase after the dev cutover; previously snake_case.
 */
export interface TreeSourceFileInfo {
  id: string;
  searchablePath: string;
  fileUrl: string | null;
  mimeType: string | null;
}

/**
 * One node in the unified tree.
 *
 * The wire format is *sparse* — optional fields are omitted entirely (not
 * emitted as ``null``) when not applicable to a given node kind:
 *
 * - ``id`` is absent on directory rows from ``/tree`` (the API has no stable
 *   id for a directory aggregate). Present on file / subdocument rows and
 *   on directory rows returned under ``fileStatusId`` drill-ins.
 * - ``fileInfo`` is absent on directory rows; present on file / subdocument
 *   rows and discriminated by ``kind``.
 * - ``fileStatusId`` is present on subdocument rows and on the engineering-view
 *   container directory returned when drilling into a file via
 *   ``?fileStatusId=...``; absent on plain directory / file rows from
 *   ``/tree``. Search results always carry it (``null`` for non-file nodes).
 * - ``sourceFile`` is only attached by ``/tree/search`` so the result row can
 *   render the containing-file breadcrumb.
 */
export interface TreeNodeResponse {
  nodeType: TreeNodeType;
  name: string;
  path: string;
  id?: string;
  hasChildren: boolean;
  fileStatusId?: string | null;
  fileInfo?: ProvenanceFileInfoBody;
  sourceFile?: TreeSourceFileInfo | null;
}

/** Response envelope for ``GET /tree`` (offset/limit paginated). */
export interface PaginatedTreeResponse {
  items: TreeNodeResponse[];
  pagination: FilesPagination;
}

/** Normalized page for ``useInfiniteQuery`` consumers. */
export interface ProvenanceTreePage {
  items: TreeNodeResponse[];
  /** ``null`` once ``hasMore === false``. */
  nextOffset: number | null;
  total: number;
}

type FetchProvenanceTreePageArgs = {
  cellId: string;
  accessToken: string;
  /** Directory path to drill into; omit for the root listing. */
  path?: string;
  /** When set, list subdocuments of the given file. */
  fileStatusId?: string;
  view?: TreeView;
  tags?: string[];
  tagMatch?: TagMatch;
  /** Optional content-category filter (same semantics as ``/tree/search``). */
  contentCategories?: string[];
  offset: number;
  limit: number;
  signal?: AbortSignal;
};

function treeQueryFromArgs(args: Omit<FetchProvenanceTreePageArgs, 'cellId' | 'accessToken' | 'signal'>): ProvenanceQuery {
  const q: ProvenanceQuery = { offset: args.offset, limit: args.limit };
  if (args.path && args.path.trim() !== '') q.path = args.path;
  if (args.fileStatusId) q.fileStatusId = args.fileStatusId;
  if (args.view) q.view = args.view;
  if (args.tags && args.tags.length > 0) q.tags = args.tags;
  if (args.tagMatch) q.tagMatch = args.tagMatch;
  if (args.contentCategories && args.contentCategories.length > 0) {
    q.contentCategories = args.contentCategories;
  }
  return q;
}

/**
 * Direct-CORS fetch for one page of ``GET /tree``. Returns a normalized
 * ``ProvenanceTreePage`` so callers can chain off ``nextOffset`` alone.
 */
export async function fetchProvenanceTreePage(
  args: FetchProvenanceTreePageArgs,
): Promise<ProvenanceTreePage> {
  const { cellId, accessToken, signal } = args;
  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: '/tree',
    query: treeQueryFromArgs(args),
    signal,
  });

  if (!body || typeof body !== 'object' || !Array.isArray((body as PaginatedTreeResponse).items)) {
    throw new ProvenanceApiError(
      'provenance-service /tree returned an unexpected body (expected PaginatedTreeResponse).',
    );
  }

  const parsed = body as PaginatedTreeResponse;
  const pagination = parsed.pagination;
  const total = typeof pagination?.total === 'number' ? pagination.total : parsed.items.length;
  const hasMore = pagination?.hasMore === true;
  const nextOffset = hasMore ? args.offset + parsed.items.length : null;
  return {
    items: parsed.items,
    nextOffset,
    total,
  };
}

type FetchProvenanceTreeSearchArgs = {
  cellId: string;
  accessToken: string;
  q?: string;
  view?: TreeView;
  contentCategories?: string[];
  tags?: string[];
  tagMatch?: TagMatch;
  limit?: number;
  signal?: AbortSignal;
};

/**
 * Direct-CORS fetch for ``GET /tree/search``. The upstream returns a flat
 * ``TreeNodeResponse[]`` (search collapses the hierarchy into results with
 * the originating ``source_file`` attached for breadcrumb rendering).
 */
export async function fetchProvenanceTreeSearch(
  args: FetchProvenanceTreeSearchArgs,
): Promise<TreeNodeResponse[]> {
  const { cellId, accessToken, signal } = args;
  const q: ProvenanceQuery = {};
  if (args.q && args.q.trim() !== '') q.q = args.q;
  if (args.view) q.view = args.view;
  if (args.contentCategories && args.contentCategories.length > 0) {
    q.contentCategories = args.contentCategories;
  }
  if (args.tags && args.tags.length > 0) q.tags = args.tags;
  if (args.tagMatch) q.tagMatch = args.tagMatch;
  if (typeof args.limit === 'number') q.limit = args.limit;

  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: '/tree/search',
    query: q,
    signal,
  });

  if (!Array.isArray(body)) {
    throw new ProvenanceApiError(
      'provenance-service /tree/search returned an unexpected body (expected TreeNodeResponse[]).',
    );
  }
  const nodes = body as TreeNodeResponse[];
  return nodes;
}

// =============================================================================
// Folders endpoint
// =============================================================================
//
// Backed by ``GET /provenance-service/v1/folders``. The endpoint expands
// every directory ancestor of any file_location for the caller's tenant into
// a flat, paginated list — i.e. it surfaces nested folders directly without
// walking the tree level-by-level. Use this for the "Folders" filter mode in
// the EvidenceLocker: each row is one folder anywhere in the hierarchy.

/** Single folder row returned by ``GET /v1/folders``. */
export interface FolderItem {
  /** Last path segment ("E-Mails" in "Enron/Manual/E-Mails"). */
  name: string;
  /** Canonical searchable path (e.g. "Enron/Manual/E-Mails"). */
  path: string;
  /** Backend always sets this to ``true``; reserved for future shaping. */
  hasChildren: boolean;
}

/** Pagination envelope returned alongside the folders ``items`` array. */
export interface FoldersPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

/** Response body for ``GET /v1/folders``. */
export interface FoldersListResponse {
  items: FolderItem[];
  pagination: FoldersPagination;
}

/** Filter set for ``GET /v1/folders``. Wire form is camelCase per backend. */
export interface ProvenanceFoldersFilters {
  searchQuery?: string;
  tags?: string[];
  tagMatch?: TagMatch;
}

/** Normalized infinite-query page shape, matching the tree/files pattern. */
export interface ProvenanceFoldersPage {
  items: FolderItem[];
  /** ``null`` once ``pagination.hasMore === false``. */
  nextOffset: number | null;
  total: number;
}

type FetchProvenanceFoldersPageArgs = {
  cellId: string;
  accessToken: string;
  offset: number;
  limit: number;
  filters: ProvenanceFoldersFilters;
  signal?: AbortSignal;
};

function foldersQueryFromFilters(
  offset: number,
  limit: number,
  filters: ProvenanceFoldersFilters,
): ProvenanceQuery {
  const q: ProvenanceQuery = { offset, limit };
  if (filters.searchQuery && filters.searchQuery.trim() !== '') {
    q.searchQuery = filters.searchQuery;
  }
  if (filters.tags && filters.tags.length > 0) q.tags = filters.tags;
  if (filters.tagMatch) q.tagMatch = filters.tagMatch;
  return q;
}

/**
 * Direct-CORS fetch for one page of ``GET /v1/folders``. Translates the
 * upstream ``{ items, pagination }`` shape into a normalized
 * ``ProvenanceFoldersPage`` for ``useInfiniteQuery``.
 */
export async function fetchProvenanceFoldersPage(
  args: FetchProvenanceFoldersPageArgs,
): Promise<ProvenanceFoldersPage> {
  const { cellId, accessToken, offset, limit, filters, signal } = args;

  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: '/folders',
    query: foldersQueryFromFilters(offset, limit, filters),
    signal,
  });

  if (
    !body ||
    typeof body !== 'object' ||
    !Array.isArray((body as FoldersListResponse).items)
  ) {
    throw new ProvenanceApiError(
      'provenance-service /folders returned an unexpected body (expected { items, pagination }).',
    );
  }

  const parsed = body as FoldersListResponse;
  const items = parsed.items;
  const pagination = parsed.pagination;
  const hasMore = pagination?.hasMore === true;
  const total = typeof pagination?.total === 'number' ? pagination.total : items.length;
  const nextOffset = hasMore ? offset + items.length : null;

  return { items, nextOffset, total };
}

// =============================================================================
// File-status endpoint
// =============================================================================

type FetchProvenanceFileStatusArgs = {
  cellId: string;
  accessToken: string;
  s3Url: string;
  signal?: AbortSignal;
};

/**
 * Look up processing status for one S3 object via ``GET /files/file-info``.
 *
 * There is no standalone ``/file-status`` route on provenance-service; status
 * lives on the file-info envelope. This helper projects that envelope into the
 * same ``FileSummary`` shape as ``GET /files`` ``items[]`` so callers can read
 * ``status`` / ``statusMessage`` uniformly.
 */
export async function fetchProvenanceFileStatus(
  args: FetchProvenanceFileStatusArgs,
): Promise<FileSummary> {
  return fileSummaryFromFileInfo(await fetchProvenanceFileInfo(args));
}

/**
 * Project a ``/files/file-info`` envelope onto ``FileSummary`` so callers can
 * read ``status`` / ``previewUrl`` / ``contentModeration`` without branching on
 * ``kind``. Subdocuments are moderated independently of their container, so a
 * flagged subdocument carries its own ``contentModeration``.
 */
function fileSummaryFromFileInfo(envelope: ProvenanceFileInfoBody): FileSummary {
  if (envelope.kind === 'file' && envelope.file) {
    return envelope.file;
  }

  if (envelope.kind === 'subdocument' && envelope.subdocument) {
    const sub = envelope.subdocument;
    return {
      createdAt: sub.createdAt,
      updatedAt: sub.updatedAt,
      id: sub.id,
      fileStatusId: sub.rootId,
      fileUuid: sub.fileUuid,
      searchablePath: sub.searchablePath,
      fileName: sub.fileName,
      fileUrl: sub.fileUrl,
      tags: [],
      contentCategories: sub.contentCategories ?? [],
      status: sub.status,
      statusMessage: sub.statusMessage ?? '',
      subdocumentCount: 0,
      subdocumentSuccessCount: 0,
      subdocumentFailureCount: 0,
      subdocumentSkipCount: 0,
      countersLastUpdatedAt: '',
      lastUpdateSentAt: '',
      md5: sub.md5,
      blake3: sub.blake3 ?? '',
      xxhash: sub.xxhash ?? '',
      size: sub.size,
      mimeType: sub.mimeType,
      extension: sub.extension,
      height: sub.height,
      width: sub.width,
      format: sub.format,
      durationInSeconds: sub.durationInSeconds,
      pageNumber: sub.pageNumber,
      pageCount: sub.pageCount,
      hasAudioStream: sub.hasAudioStream,
      hasVideoStream: sub.hasVideoStream,
      hasTranscript: sub.hasTranscript,
      isImage: sub.isImage,
      previewUrl: sub.previewUrl,
      downloadUrl: sub.downloadUrl,
      transcriptUrl: sub.transcriptUrl,
      contentModeration: sub.contentModeration ?? null,
    };
  }

  throw new ProvenanceApiError(
    'provenance-service /files/file-info returned an unexpected body (expected file or subdocument).',
  );
}

// =============================================================================
// File-info endpoint
// =============================================================================
//
// After the dev cutover, ``GET /v1/files/file-info`` returns a discriminated
// union by ``kind``:
//
//   { kind: 'file',        file: ProvenanceFileInfoFile,        hierarchy: ... }
//   { kind: 'subdocument', subdocument: ProvenanceSubdocumentInfo, hierarchy: ... }
//
// The same envelope is *embedded* (without ``hierarchy`` populated — it's
// always ``null`` there) into every file / subdocument node returned by
// ``/tree`` and ``/tree/search`` via ``TreeNodeResponse.fileInfo``.

/**
 * Per-file info, identical in shape to the items returned by ``GET /v1/files``
 * — re-exported here as an alias so the file-info endpoint and the tree
 * endpoint share one canonical type.
 */
export type ProvenanceFileInfoFile = FileSummary;

/**
 * Per-subdocument info returned when the resolved URL points at a processed
 * subdocument (e.g. the spreadsheet-normalization output of a CSV). Distinct
 * shape from ``ProvenanceFileInfoFile`` — no ``fileStatusId``/``tags``/
 * ``subdocumentCount`` family, but adds ``rootId``, ``userPath``, ``fullPath``,
 * and ``isUserVisible``.
 */
export interface ProvenanceSubdocumentInfo {
  createdAt: string;
  updatedAt: string;
  id: string;
  rootId: string;
  userPath: string;
  fullPath: string;
  isUserVisible: boolean;
  md5: string;
  fileUuid: string;
  fileUrl: string;
  searchablePath: string;
  fileName: string;
  size: number;
  mimeType: string;
  extension: string;
  contentCategories: string[];
  blake3: string;
  xxhash: string;
  height: number | null;
  width: number | null;
  format: string | null;
  durationInSeconds: number | null;
  /** Page number when the file represents one page of a larger document; null otherwise. */
  pageNumber: number | null;
  pageCount: number | null;
  hasAudioStream: boolean | null;
  hasVideoStream: boolean | null;
  hasTranscript: boolean | null;
  isImage: boolean | null;
  previewUrl: string | null;
  downloadUrl: string | null;
  transcriptUrl: string | null;
  status: string;
  statusMessage?: string | null;
  /**
   * Content-moderation metadata for the extracted subdocument. Subdocuments
   * (e.g. images extracted from an archive) are moderated independently of
   * their container, so this is populated per-subdocument just like on a
   * top-level file.
   */
  contentModeration?: ContentModeration | null;
}

/**
 * One entry in the ``hierarchy`` breadcrumb returned by ``/files/file-info``
 * (always ``null`` on the embedded ``fileInfo`` inside tree nodes). Walks
 * from root source file → enclosing subdocs → the resolved target.
 */
export interface HierarchyItem {
  kind: 'file' | 'subdocument';
  id: string;
  s3Url: string;
  fileName: string;
  mimeType: string;
}

/**
 * Discriminated union returned by ``GET /v1/files/file-info`` and embedded
 * (with ``hierarchy: null``) on file / subdocument tree nodes.
 */
export type ProvenanceFileInfoBody =
  | { kind: 'file'; file: ProvenanceFileInfoFile; hierarchy: HierarchyItem[] | null }
  | {
      kind: 'subdocument';
      subdocument: ProvenanceSubdocumentInfo;
      hierarchy: HierarchyItem[] | null;
    };

type FetchProvenanceFileInfoArgs = {
  cellId: string;
  accessToken: string;
  /** S3-style URL (``s3://<bucket>/<key>``) or relative key — passed through as the upstream ``url`` query param. */
  s3Url: string;
  /** When true, the upstream ``?bypass_cm=true`` param is added so the server skips the CM preview override. */
  bypassCm?: boolean;
  signal?: AbortSignal;
};

/**
 * Direct-CORS fetch for ``GET /v1/files/file-info?url=<s3>``. Returns the
 * discriminated ``{ kind, file | subdocument, hierarchy }`` envelope.
 * ``MetaContext`` reads ``md5`` straight off ``file`` / ``subdocument`` to
 * build prep paths; ``getFileInfoFromServer`` (functions.ts) projects the
 * envelope into the canonical ``FileSummary`` shape consumed by the UI.
 */
export async function fetchProvenanceFileInfo(
  args: FetchProvenanceFileInfoArgs,
): Promise<ProvenanceFileInfoBody> {
  const { cellId, accessToken, s3Url, bypassCm, signal } = args;
  const body = await provenanceFetch<ProvenanceFileInfoBody>({
    cellId,
    accessToken,
    path: '/files/file-info',
    query: { url: s3Url, ...(bypassCm ? { bypass_cm: 'true' } : {}) },
    signal,
  });
  return body;
}

/**
 * Envelope from ``POST /v1/files/file-info``.
 *
 * ``results`` is keyed by the URL **as submitted** (not the normalized form),
 * so callers can index back into the list they sent. Each value is the same
 * discriminated union ``GET /files/file-info`` returns for one URL.
 */
export interface BatchProvenanceFileInfoResponse {
  results: Record<string, ProvenanceFileInfoBody>;
}

type FetchProvenanceFileInfoBatchArgs = {
  cellId: string;
  accessToken: string;
  /**
   * S3 URLs to resolve in one call. The route accepts 1–500; callers chunk
   * larger lists. ``bypass_cm`` is not supported on the batch route.
   */
  s3Urls: string[];
  signal?: AbortSignal;
};

/**
 * Direct-CORS fetch for ``POST /v1/files/file-info``.
 *
 * One round-trip resolves every submitted URL. The route fails the whole
 * batch when any single URL is invalid or missing (no partial ``results``),
 * so callers that need per-URL isolation split and retry.
 */
export async function fetchProvenanceFileInfoBatch(
  args: FetchProvenanceFileInfoBatchArgs,
): Promise<BatchProvenanceFileInfoResponse> {
  const { cellId, accessToken, s3Urls, signal } = args;
  return provenancePost<BatchProvenanceFileInfoResponse>({
    cellId,
    accessToken,
    path: '/files/file-info',
    body: { urls: s3Urls },
    signal,
  });
}

/** Most URLs ``POST /v1/files/file-info`` accepts in one request. */
export const PROVENANCE_FILE_INFO_BATCH_LIMIT = 500;

// =============================================================================
// Status counts endpoint
// =============================================================================

/**
 * Status counts response: ``{ responses: [{ counts: { <status>: <count> } }] }``.
 * Kept loose because consumers only read the first ``responses[0].counts``
 * map; additional fields are tolerated.
 */
export interface ProvenanceStatusCountsResponse {
  responses?: Array<{ counts?: Record<string, number> }>;
  [key: string]: unknown;
}

type FetchProvenanceStatusCountsArgs = {
  cellId: string;
  accessToken: string;
  signal?: AbortSignal;
};

/** Direct-CORS fetch for ``GET /v1/status_counts``. */
export async function fetchProvenanceStatusCounts(
  args: FetchProvenanceStatusCountsArgs,
): Promise<ProvenanceStatusCountsResponse> {
  const { cellId, accessToken, signal } = args;
  return provenanceFetch<ProvenanceStatusCountsResponse>({
    cellId,
    accessToken,
    path: '/status_counts',
    signal,
  });
}

// =============================================================================
// Content moderation endpoints
// =============================================================================

/**
 * Detection type a content moderation finding belongs to. Mirrors the backend
 * ``DetectionType`` enum. Required by the summary, findings, and status-update
 * endpoints — the report UI passes ``"CSAM"``.
 */
export type DetectionType = 'CSAM' | 'NSFW';

/**
 * Bounding box as fractions of image width/height in ``[0, 1]``, matching
 * Rekognition's facial-recognition shape (``left`` / ``top`` / ``width`` /
 * ``height``).
 */
export interface NormalizedBoundingBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * A single detection from the NudeNet detector as stored in ``nudenet.json``.
 * Newer files use a normalized ``box`` object; older files may still use a
 * pixel ``[x, y, w, h]`` array.
 */
export interface NudeNetDetection {
  class: string;
  score: number;
  box: NormalizedBoundingBox | number[];
}

/**
 * One detection run: which method was used, where its raw output lives in S3,
 * and the detections fetched from that file.
 */
export interface DetectionResult {
  type: string;
  /** Raw detector output fetched from S3 and inlined by the API; null if unavailable. */
  detections: NudeNetDetection[] | null;
  /** Detector confidence in [0, 1]. Null on findings written before scores were stored. */
  score?: number | null;
}

/**
 * Single finding returned by ``GET /v1/content-moderation/findings``.
 * Field names mirror the backend ``ContentModerationFindingResponse`` (camelCase
 * after alias serialisation).
 *
 * - ``findingStatus`` — one of ``"FLAGGED"`` | ``"REVIEWED"`` | ``"CONFIRMED"``.
 * - ``detectionType`` — type of content detected (e.g. ``"CSAM"``).
 * - ``creationMethod`` — how the finding was created:
 *   ``"DATA_INGESTION"`` | ``"MANUAL"``.
 * - ``detectionResults`` — one entry per detection run with raw detector output
 *   fetched inline from S3.
 * - ``obfuscatedUrl`` — blurred / redacted copy of the source (may be null).
 */
export interface ContentModerationFinding {
  filePath: string;
  fileUuid: string;
  fileMd5: string;
  findingStatus: string;
  flaggedAt: string;
  detectionType: string;
  creationMethod: string;
  detectionResults: DetectionResult[];
  obfuscatedUrl: string | null;
  remediationApplied: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  /**
   * LLM-generated natural-language description of the flagged image. Lets a
   * reviewer decide whether content is CSAM before revealing the original.
   * Nullable — not every finding has a generated description.
   */
  imageDescription: string | null;
  /**
   * Optional display name of the case user who created a MANUAL finding.
   * TR-3033 does not require this on the finding; Dashboard falls back to
   * "Flagged by user". Actor identity for MANUAL ``FINDING_CREATED`` lives
   * on the audit row (``email`` / ``user_id``).
   */
  flaggedBy?: string | null;
}

/**
 * One row of ``GET /v1/content-moderation/findings``. DynamoDB only: no
 * detector JSON and no image description.
 */
export interface ContentModerationFindingSummary {
  fileMd5: string;
  detectionType: string;
  filePath: string;
  findingStatus: string;
  flaggedAt: string;
  creationMethod: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  obfuscatedUrl: string | null;
  flaggedBy?: string | null;
}

/**
 * ``GET /v1/content-moderation/findings/{detectionType}/{fileMd5}``.
 * The summary plus the S3-backed description, boxes, and per-detector scores.
 */
export interface ContentModerationFindingDetail extends ContentModerationFindingSummary {
  fileUuid: string;
  detectionResults: DetectionResult[];
  imageDescription: string | null;
  remediationApplied: string | null;
  /** Highest detector score, or null when none was stored. */
  confidence: number | null;
  manualObfuscationJobStatus?: string | null;
}

/** Paginated envelope for ``GET /v1/content-moderation/findings``. */
export interface ContentModerationFindingsPage {
  items: ContentModerationFindingSummary[];
  /** Opaque cursor; ``null`` once the last page has been reached. */
  nextToken: string | null;
}

type FetchContentModerationFindingsPageArgs = {
  cellId: string;
  accessToken: string;
  caseId: string;
  /** Detection type to list findings for (e.g. ``"CSAM"``). */
  detectionType: DetectionType;
  /** Restrict to a single status: ``"FLAGGED"``, ``"REVIEWED"``, or ``"CONFIRMED"``. */
  status?: string;
  limit?: number;
  /** Pagination cursor from the previous page's ``nextToken``. */
  nextToken?: string | null;
  signal?: AbortSignal;
};

/** The three valid backend status values for a content moderation finding. */
export type FindingStatusValue = 'FLAGGED' | 'REVIEWED' | 'CONFIRMED';

type UpdateContentModerationFindingStatusArgs = {
  cellId: string;
  accessToken: string;
  /** ``detection_type`` path parameter (e.g. ``"CSAM"``). */
  detectionType: DetectionType;
  /** ``file_md5`` path parameter. */
  fileMd5: string;
  /**
   * ``case_id`` query parameter. Required: a finding is keyed by
   * (case, document, detection type), so the same image flagged in two cases
   * has a separate finding — and separate review state — per case.
   */
  caseId: string;
  status: FindingStatusValue;
  signal?: AbortSignal;
};

/**
 * Direct-CORS PUT to
 * ``/v1/content-moderation/findings/{detectionType}/{fileMd5}/status?case_id=…``.
 *
 * Atomically updates the finding's status and shifts the case summary
 * counters (handled server-side via a DynamoDB TransactWriteItems). Returns
 * the updated finding summary.
 */
export async function updateContentModerationFindingStatus(
  args: UpdateContentModerationFindingStatusArgs,
): Promise<ContentModerationFindingSummary> {
  const { cellId, accessToken, detectionType, fileMd5, caseId, status, signal } = args;
  return provenancePut<ContentModerationFindingSummary>({
    cellId,
    accessToken,
    path: `/content-moderation/findings/${detectionType}/${fileMd5}/status`,
    query: { case_id: caseId },
    body: { status },
    signal,
  });
}

type CreateContentModerationFindingArgs = {
  cellId: string;
  accessToken: string;
  caseId: string;
  fileUrl: string;
  detectionType?: DetectionType;
  signal?: AbortSignal;
};

/**
 * Direct-CORS POST to ``/v1/content-moderation/findings`` (TR-3033).
 * Body is ``{ caseId, fileUrl, detectionType }``. Case access, not Verifier
 * clearance. Lands as ``creationMethod=MANUAL``, ``findingStatus=CONFIRMED``,
 * ``obfuscatedUrl`` unset until the blur job finishes. Images only. A 409
 * means a finding already has an obfuscated copy. Provenance does not write
 * a new finding or start a blur job in that case.
 */
export async function createContentModerationFinding(
  args: CreateContentModerationFindingArgs,
): Promise<ContentModerationFinding> {
  const { cellId, accessToken, caseId, fileUrl, detectionType = 'CSAM', signal } = args;
  return provenancePost<ContentModerationFinding>({
    cellId,
    accessToken,
    path: '/content-moderation/findings',
    body: { caseId, fileUrl, detectionType },
    signal,
  });
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

export type ObfuscationPollStep = 'skip-stale' | 'cleared' | 'failed' | 'ready' | 'continue';

/**
 * One file-info read during a blur poll.
 * A re-flag (`awaitingReflag`) skips the leftover REVIEWED row until the new
 * job is IN_PROGRESS. Any other REVIEWED row is a real clear and must not
 * keep the obfuscated preview.
 */
export function nextObfuscationPollStep(input: {
  findingStatus?: string | null;
  jobStatus?: string | null;
  previewUrl?: string | null;
  hasModerationType?: boolean;
  awaitingReflag?: boolean;
  seenInProgress?: boolean;
}): ObfuscationPollStep {
  const reviewed = input.findingStatus === 'REVIEWED';
  const inProgress = input.jobStatus === 'IN_PROGRESS';
  if (reviewed && input.awaitingReflag && !input.seenInProgress && !inProgress) {
    return 'skip-stale';
  }
  if (reviewed && !input.awaitingReflag) return 'cleared';
  if (reviewed && input.seenInProgress && !inProgress) return 'cleared';
  if (input.jobStatus === 'FAILED') return 'failed';
  if (
    input.previewUrl?.trim() &&
    (input.jobStatus === 'COMPLETED' || (input.jobStatus !== 'IN_PROGRESS' && input.hasModerationType))
  ) {
    return 'ready';
  }
  return 'continue';
}

export type ObfuscationPollResult = {
  previewUrl: string | null;
  finding?: ContentModerationFinding;
  jobStatus?: ManualObfuscationJobStatus | null;
  /** Job was still running when ``maxAttempts`` ran out; not a failure. */
  timedOut?: boolean;
  /** Verifier cleared the finding. Do not show ``previewUrl``. */
  cleared?: boolean;
};

/**
 * Poll ``GET /files/file-info`` until the manual blur job finishes.
 *
 * TR-3033 writes ``obfuscated_url`` only after the async job. File-info
 * exposes that as ``contentModeration.manualObfuscationJobStatus``:
 * ``IN_PROGRESS`` keeps the spinner up; ``COMPLETED`` and ``FAILED`` are
 * terminal. Ingest findings omit the field — fall back to ``previewUrl`` +
 * type. Running out of attempts returns ``timedOut`` rather than throwing, so
 * a slow worker reads differently from a failed one.
 */
export async function pollContentModerationObfuscation(args: {
  cellId: string;
  accessToken: string;
  fileUrl: string;
  /** Skip a leftover REVIEWED row until the new blur job is visible. */
  awaitingReflag?: boolean;
  intervalMs?: number;
  maxAttempts?: number;
  signal?: AbortSignal;
}): Promise<ObfuscationPollResult> {
  const { cellId, accessToken, fileUrl, signal } = args;
  const intervalMs = args.intervalMs ?? 2000;
  const maxAttempts = args.maxAttempts ?? 60;
  const awaitingReflag = Boolean(args.awaitingReflag);
  let jobStatus: ManualObfuscationJobStatus | null = null;
  let previewUrl: string | null = null;
  let seenInProgress = false;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const file = fileSummaryFromFileInfo(
      await fetchProvenanceFileInfo({
        cellId,
        accessToken,
        s3Url: fileUrl,
        signal,
      }),
    );
    const cm = file.contentModeration ?? null;
    jobStatus = cm?.manualObfuscationJobStatus ?? null;
    previewUrl = file.previewUrl?.trim() || null;
    const step = nextObfuscationPollStep({
      findingStatus: cm?.findingStatus,
      jobStatus,
      previewUrl,
      hasModerationType: Boolean(cm?.type),
      awaitingReflag,
      seenInProgress,
    });
    if (jobStatus === 'IN_PROGRESS') seenInProgress = true;
    if (step === 'skip-stale') {
      await sleep(intervalMs, signal);
      continue;
    }
    if (step === 'cleared') return { previewUrl: null, jobStatus, cleared: true };
    if (step === 'failed') return { previewUrl, jobStatus };
    if (step === 'ready') return { previewUrl, jobStatus };
    await sleep(intervalMs, signal);
  }
  return { previewUrl, jobStatus, timedOut: true };
}

// =============================================================================
// Stats endpoint
// =============================================================================
//
// Backed by ``GET /provenance-service/v1/stats``. Returns the tenant's total
// ingested-byte usage and quota, plus a per-case byte breakdown. Drives the
// Account-view usage meter.

/** One per-case usage row returned in ``ProvenanceStatsResponse.bytesPerCase``. */
export interface ProvenanceStatsCaseBytes {
  caseName: string;
  caseTag: string;
  totalBytes: number;
}

/**
 * Response body for ``GET /provenance-service/v1/stats`` (tenant usage + quota).
 *
 * The meter currently only consumes ``totalSizeBytes`` (usage) and
 * ``quotaLimitBytes`` (cap); the remaining fields are typed for completeness
 * and future per-case / file-vs-subdocument breakdowns.
 */
export interface ProvenanceStatsResponse {
  totalFileSize: number;
  totalSubdocumentSize: number;
  totalSizeBytes: number;
  quotaLimitBytes: number;
  bytesPerCase: ProvenanceStatsCaseBytes[];
}

type FetchProvenanceStatsArgs = {
  cellId: string;
  accessToken: string;
  signal?: AbortSignal;
};

/** Direct-CORS fetch for ``GET /v1/stats``. */
export async function fetchProvenanceStats(
  args: FetchProvenanceStatsArgs,
): Promise<ProvenanceStatsResponse> {
  const { cellId, accessToken, signal } = args;
  return provenanceFetch<ProvenanceStatsResponse>({
    cellId,
    accessToken,
    path: '/stats',
    signal,
  });
}

/** Direct-CORS fetch for one page of ``GET /v1/content-moderation/findings``. */
export async function fetchContentModerationFindingsPage(
  args: FetchContentModerationFindingsPageArgs,
): Promise<ContentModerationFindingsPage> {
  const { cellId, accessToken, caseId, detectionType, status, limit = 50, nextToken, signal } =
    args;

  const query: ProvenanceQuery = {
    case_id: caseId,
    detection_type: detectionType,
    limit,
    // The list still hydrates detector JSON from S3 unless this is set.
    // Remove it when provenance drops that default (TR-5062).
    skipDetails: true,
  };
  if (status) query.status = status;
  if (nextToken) query.nextToken = nextToken;

  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: '/content-moderation/findings',
    query,
    signal,
  });

  if (
    !body ||
    typeof body !== 'object' ||
    !Array.isArray((body as ContentModerationFindingsPage).items)
  ) {
    throw new ProvenanceApiError(
      'provenance-service /content-moderation/findings returned an unexpected body.',
    );
  }

  return body as ContentModerationFindingsPage;
}

type FetchContentModerationFindingDetailArgs = {
  cellId: string;
  accessToken: string;
  caseId: string;
  detectionType: DetectionType;
  fileMd5: string;
  signal?: AbortSignal;
};

/** Direct-CORS fetch for one finding's detector JSON and image description. */
export async function fetchContentModerationFindingDetail(
  args: FetchContentModerationFindingDetailArgs,
): Promise<ContentModerationFindingDetail> {
  const { cellId, accessToken, caseId, detectionType, fileMd5, signal } = args;
  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: `/content-moderation/findings/${detectionType}/${fileMd5}`,
    query: { case_id: caseId },
    signal,
  });
  if (!body || typeof body !== 'object' || !('fileMd5' in body)) {
    throw new ProvenanceApiError(
      'provenance-service /content-moderation/findings/{type}/{md5} returned an unexpected body.',
    );
  }
  return body as ContentModerationFindingDetail;
}

/**
 * Summary counts returned by ``GET /v1/content-moderation/summary``.
 *
 * - ``flaggedCount``   — items still waiting for manual review (status FLAGGED).
 * - ``reviewedCount``  — items that have been reviewed but not confirmed (status REVIEWED).
 * - ``confirmedCount`` — items confirmed as CSAM (status CONFIRMED).
 */
export interface ContentModerationSummary {
  caseId: string;
  flaggedCount: number;
  reviewedCount: number;
  confirmedCount: number;
}

type FetchContentModerationSummaryArgs = {
  cellId: string;
  accessToken: string;
  caseId: string;
  /** Detection type to summarise (e.g. ``"CSAM"``). */
  detectionType: DetectionType;
  signal?: AbortSignal;
};

/** Direct-CORS fetch for ``GET /v1/content-moderation/summary``. */
export async function fetchContentModerationSummary(
  args: FetchContentModerationSummaryArgs,
): Promise<ContentModerationSummary> {
  const { cellId, accessToken, caseId, detectionType, signal } = args;
  const body = await provenanceFetch<unknown>({
    cellId,
    accessToken,
    path: '/content-moderation/summary',
    query: { case_id: caseId, detection_type: detectionType },
    signal,
  });
  if (!body || typeof body !== 'object') {
    throw new ProvenanceApiError(
      'provenance-service /content-moderation/summary returned an unexpected body.',
    );
  }
  return body as ContentModerationSummary;
}

// =============================================================================
// Query keys
// =============================================================================

/**
 * Hierarchical TanStack Query key factory for every provenance-service
 * resource. Use the most specific key in each hook so partial invalidation
 * (``queryClient.invalidateQueries({ queryKey: provenanceKeys.files() })``) is
 * straightforward.
 */
export const provenanceKeys = {
  all: ['provenance'] as const,
  cases: () => [...provenanceKeys.all, 'cases'] as const,
  casesList: (cellId: string | null) => [...provenanceKeys.cases(), 'list', cellId] as const,
  files: () => [...provenanceKeys.all, 'files'] as const,
  filesList: (cellId: string | null, filters: ProvenanceFilesFilters, pageSize: number) =>
    [...provenanceKeys.files(), 'list', cellId, pageSize, filters] as const,
  fileStatus: () => [...provenanceKeys.all, 'fileStatus'] as const,
  fileStatusForUrl: (cellId: string | null, s3Url: string) =>
    [...provenanceKeys.fileStatus(), cellId, s3Url] as const,
  fileInfo: () => [...provenanceKeys.all, 'fileInfo'] as const,
  fileInfoForUrl: (cellId: string | null, s3Url: string) =>
    [...provenanceKeys.fileInfo(), cellId, s3Url] as const,
  statusCounts: () => [...provenanceKeys.all, 'statusCounts'] as const,
  statusCountsForCell: (cellId: string | null) =>
    [...provenanceKeys.statusCounts(), cellId] as const,
  stats: () => [...provenanceKeys.all, 'stats'] as const,
  statsForCell: (cellId: string | null) => [...provenanceKeys.stats(), cellId] as const,
  tree: () => [...provenanceKeys.all, 'tree'] as const,
  treeList: (
    cellId: string | null,
    params: {
      path?: string;
      fileStatusId?: string;
      view?: TreeView;
      tags?: string[];
      tagMatch?: TagMatch;
      contentCategories?: string[];
      pageSize: number;
    },
  ) => [...provenanceKeys.tree(), 'list', cellId, params] as const,
  treeSearch: () => [...provenanceKeys.tree(), 'search'] as const,
  treeSearchFor: (
    cellId: string | null,
    params: {
      q?: string;
      view?: TreeView;
      contentCategories?: string[];
      tags?: string[];
      tagMatch?: TagMatch;
      limit?: number;
    },
  ) => [...provenanceKeys.treeSearch(), cellId, params] as const,
  folders: () => [...provenanceKeys.all, 'folders'] as const,
  foldersList: (
    cellId: string | null,
    filters: ProvenanceFoldersFilters,
    pageSize: number,
  ) => [...provenanceKeys.folders(), 'list', cellId, pageSize, filters] as const,
  contentModeration: () => [...provenanceKeys.all, 'content-moderation'] as const,
  contentModerationSummary: (
    cellId: string | null,
    caseId: string | null,
    detectionType: DetectionType,
  ) =>
    [...provenanceKeys.contentModeration(), 'summary', cellId, caseId, detectionType] as const,
  contentModerationFindings: (
    cellId: string | null,
    caseId: string | null,
    detectionType: DetectionType,
    status: string | null,
    pageSize: number,
  ) =>
    [
      ...provenanceKeys.contentModeration(),
      'findings',
      cellId,
      caseId,
      detectionType,
      status,
      pageSize,
    ] as const,
  contentModerationFindingDetail: (
    cellId: string | null,
    caseId: string | null,
    detectionType: DetectionType,
    fileMd5: string | null,
  ) =>
    [
      ...provenanceKeys.contentModeration(),
      'finding-detail',
      cellId,
      caseId,
      detectionType,
      fileMd5,
    ] as const,
  contentModerationImageDescription: (
    cellId: string | null,
    caseId: string | null,
    detectionType: DetectionType | null,
    fileMd5: string | null,
    filePath: string | null,
  ) =>
    [
      ...provenanceKeys.contentModeration(),
      'image-description',
      cellId,
      caseId,
      detectionType,
      fileMd5,
      filePath,
    ] as const,
};
